import { z } from 'zod';
import { i18nTextSchema } from '../common/i18n';

/**
 * Portal branding — the one authorable surface that both apps read before a
 * user is known: logo, login scenery, hero copy and the two brand golds.
 *
 * It lives in `system_settings` under a single key so a read is one indexed
 * lookup, and so publishing is atomic: an operator never catches the portal
 * half-rebranded. Images are NOT inlined here — uploads are transcoded to
 * WebP/AVIF renditions in the encrypted blob store and referenced by id, which
 * keeps this document small enough to ship on every cold page load.
 */
export const BRANDING_SETTING_KEY = 'branding';

/** Widths the upload pipeline renders, in the order `srcset` should list them. */
export const BRANDING_BACKGROUND_WIDTHS = [640, 1024, 1600, 2400] as const;
export const BRANDING_LOGO_WIDTHS = [96, 192, 384] as const;

export const brandingImageFormatSchema = z.enum(['webp', 'avif']);
export type BrandingImageFormat = z.infer<typeof brandingImageFormatSchema>;

/**
 * Encoding-quality preset chosen at upload time, applied to every rendition of
 * that upload. A preset rather than a raw 1–100 number: the WebP and AVIF
 * quality scales are not comparable (avif 52 ≈ webp 74), and logos are always
 * encoded sharper than the background, so one number cannot say what the
 * operator means. `balanced` is the historical default.
 */
export const brandingImageQualitySchema = z.enum(['compact', 'balanced', 'best']);
export type BrandingImageQuality = z.infer<typeof brandingImageQualitySchema>;

/** Formats an operator may upload. SVG is excluded on purpose: it is script-
 *  capable markup, and a logo that can run JS on the login page is an XSS
 *  vector, not an asset. */
export const BRANDING_UPLOAD_MIME = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
] as const;

export const brandingAssetKindSchema = z.enum(['logo', 'logoDark', 'background']);
export type BrandingAssetKind = z.infer<typeof brandingAssetKindSchema>;

export const brandingImageVariantSchema = z.object({
  /** Opaque, content-addressed id — also the immutable cache key of its URL. */
  assetId: z.string().min(8).max(64),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  format: brandingImageFormatSchema,
  bytes: z.number().int().nonnegative(),
});
export type BrandingImageVariant = z.infer<typeof brandingImageVariantSchema>;

export const brandingImageSchema = z.object({
  /** Intrinsic size of the largest rendition — fixes the aspect box, no CLS. */
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  /** Every rendition, any format, ascending width. The client groups by format. */
  variants: z.array(brandingImageVariantSchema).min(1).max(16),
  /**
   * ~20px-wide WebP as a data URI. Painted underneath the real image so the
   * hero has colour on the first frame instead of a flash of empty scrim.
   * Capped hard: this rides in the branding document on every page load.
   */
  placeholder: z.string().max(3000).optional(),
  /** Average colour of the source, used as the box fill before any decode. */
  dominant: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  alt: z.string().max(200).optional(),
  originalName: z.string().max(200).optional(),
  uploadedAt: z.string().optional(),
});
export type BrandingImage = z.infer<typeof brandingImageSchema>;

/**
 * Hero feature tiles. Never free markup — the authoring surface must not be
 * able to inject anything that renders on the anonymous login page.
 *
 * Two safe forms:
 * - a code from the built-in set below (the original closed registry), or
 * - any icon picked in the studio's MUI catalogue, persisted as its name plus
 *   the glyph's 24×24 `<path d>` geometry in `iconPaths`. Path data is inert:
 *   it is validated against the path-grammar alphabet (no quotes, no angle
 *   brackets, no URLs) and only ever rendered as a `d` attribute, so it can
 *   draw shapes but cannot carry markup, styles or script.
 */
export const BRANDING_FEATURE_ICONS = [
  'certificate',
  'track',
  'shield',
  'translate',
  'studio',
  'approvals',
  'reports',
  'security',
  'clock',
  'support',
] as const;
export const brandingFeatureIconSchema = z.enum(BRANDING_FEATURE_ICONS);
export type BrandingFeatureIcon = z.infer<typeof brandingFeatureIconSchema>;

/** SVG path data (`<path d>`): commands, digits, separators — nothing else. */
export const svgPathDataSchema = z
  .string()
  .min(1)
  .max(4000)
  .regex(/^[MmZzLlHhVvCcSsQqTtAa0-9\s,.eE+-]+$/, 'expected SVG path data');

export const brandingFeatureSchema = z.object({
  /** Built-in code, or the picked icon's name (informational once iconPaths is set). */
  icon: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-zA-Z][a-zA-Z0-9]*$/, 'expected an icon name'),
  /** Custom glyph geometry from the icon picker; renders instead of `icon`. */
  iconPaths: z.array(svgPathDataSchema).min(1).max(6).optional(),
  label: i18nTextSchema,
});
export type BrandingFeature = z.infer<typeof brandingFeatureSchema>;

/** Everything written on one portal's login scenery. */
export const brandingLoginContentSchema = z.object({
  /** Small gold kicker above the title. */
  welcome: i18nTextSchema,
  /** Two-line hero title; the second line renders in gold. */
  titleTop: i18nTextSchema,
  titleBottom: i18nTextSchema,
  /** Mission lines under the title. */
  lines: z.array(i18nTextSchema).max(4).default([]),
  features: z.array(brandingFeatureSchema).max(4).default([]),
  /** Card heading + sub-line, shown beside the logo inside the auth box. */
  cardTitle: i18nTextSchema,
  cardTagline: i18nTextSchema,
  /** Optional reassurance line at the foot of the card. */
  footnote: i18nTextSchema.optional(),
});
export type BrandingLoginContent = z.infer<typeof brandingLoginContentSchema>;

const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'expected a #rrggbb colour');

export const brandingThemeSchema = z.object({
  /** Brand gold — fills, borders, focus rings, active states. */
  gold: hexColorSchema,
  /** Deeper gold — gradient end and hover. */
  goldDeep: hexColorSchema,
  /**
   * Same hue darkened until small text clears 4.5:1 on a white surface. Kept
   * explicit rather than computed so an operator who changes `gold` cannot
   * silently drop the portal below AA.
   */
  goldInk: hexColorSchema,
  /** The dark-scheme counterpart of `goldInk`. */
  goldInkDark: hexColorSchema,
});
export type BrandingTheme = z.infer<typeof brandingThemeSchema>;

export const brandingSchema = z.object({
  version: z.literal(1).default(1),
  appName: i18nTextSchema,
  orgName: i18nTextSchema,
  adminAppName: i18nTextSchema,
  logo: brandingImageSchema.nullable().default(null),
  /** Optional light-ink logo for the dark scheme; falls back to `logo`. */
  logoDark: brandingImageSchema.nullable().default(null),
  /** Login hero photograph; falls back to the built-in vector scenery. */
  background: brandingImageSchema.nullable().default(null),
  theme: brandingThemeSchema,
  /**
   * Whether the portals offer the English ⇄ हिन्दी control at all — the app-bar
   * toggle and the switch on both login sceneries. Hiding it does not touch a
   * language a visitor already chose; it only removes the control, so a
   * deployment that authors one language stops advertising a choice it
   * doesn't honour.
   */
  showLanguageSwitcher: z.boolean().default(true),
  login: z.object({
    citizen: brandingLoginContentSchema,
    admin: brandingLoginContentSchema,
  }),
});
export type Branding = z.infer<typeof brandingSchema>;

/**
 * PUT body: any subset, deep-merged over the stored document.
 *
 * The image fields are re-declared WITHOUT `.default(null)` before `.partial()`.
 * Zod applies a field's default even under `.partial()`, so the plain
 * `brandingSchema.partial()` used to materialise `logo/logoDark/background: null`
 * into every parsed patch that omitted them — and an explicit null is the
 * "clear this image" instruction to the deep merge. Publishing login copy would
 * silently reset all three uploads.
 *
 * `showLanguageSwitcher` is re-declared for the same reason: its `.default(true)`
 * would ride into every unrelated patch and flip a switched-off control back on.
 */
export const brandingUpdateSchema = brandingSchema
  .omit({ version: true })
  .extend({
    logo: brandingImageSchema.nullable(),
    logoDark: brandingImageSchema.nullable(),
    background: brandingImageSchema.nullable(),
    showLanguageSwitcher: z.boolean(),
  })
  .partial();
export type BrandingUpdateInput = z.infer<typeof brandingUpdateSchema>;

export const brandingUploadFieldsSchema = z.object({
  kind: brandingAssetKindSchema,
  format: brandingImageFormatSchema.optional(),
  quality: brandingImageQualitySchema.optional(),
  alt: z.string().max(200).optional(),
});

export interface BrandingResponseDto {
  branding: Branding;
  /** Absolute base URL to prefix an `assetId` with. */
  assetBaseUrl: string;
  /** Changes whenever the document does — lets clients revalidate cheaply. */
  etag: string;
}

export interface BrandingAssetResponseDto {
  kind: BrandingAssetKind;
  image: BrandingImage;
}

/**
 * Brand golds from the approved palette: rgb(212,160,23) and rgb(199,139,45).
 * The two ink shades are those hues darkened/lightened to clear AA for text on
 * white and on black respectively — see brandingThemeSchema.
 */
export const DEFAULT_BRANDING_THEME: BrandingTheme = {
  gold: '#d4a017',
  goldDeep: '#c78b2d',
  goldInk: '#8a6410',
  goldInkDark: '#e8bd52',
};

const CITIZEN_LOGIN: BrandingLoginContent = {
  welcome: { en: 'Welcome To', hi: 'आपका स्वागत है' },
  titleTop: { en: 'Citizen Services', hi: 'नागरिक सेवा' },
  titleBottom: { en: 'Portal', hi: 'पोर्टल' },
  lines: [
    {
      en: 'Apply for certificates and licenses online,',
      hi: 'प्रमाण पत्र और लाइसेंस ऑनलाइन पाएं,',
    },
    { en: 'track every application in real time,', hi: 'हर आवेदन को वास्तविक समय में ट्रैक करें,' },
    {
      en: 'and receive signed documents digitally.',
      hi: 'और हस्ताक्षरित दस्तावेज़ डिजिटल रूप से पाएं।',
    },
  ],
  features: [
    { icon: 'certificate', label: { en: 'Certificates & Licenses', hi: 'प्रमाण पत्र और लाइसेंस' } },
    { icon: 'track', label: { en: 'Real-time Tracking', hi: 'रीयल-टाइम ट्रैकिंग' } },
    { icon: 'shield', label: { en: 'Secure & Verified', hi: 'सुरक्षित और सत्यापित' } },
    { icon: 'translate', label: { en: 'हिन्दी + English', hi: 'हिन्दी + English' } },
  ],
  cardTitle: { en: 'Citizen Services Portal', hi: 'नागरिक सेवा पोर्टल' },
  cardTagline: { en: 'Apply • Track • Receive', hi: 'आवेदन • ट्रैक • प्राप्त' },
  footnote: {
    en: 'Secured by one-time password verification',
    hi: 'वन-टाइम पासवर्ड सत्यापन द्वारा सुरक्षित',
  },
};

const ADMIN_LOGIN: BrandingLoginContent = {
  welcome: { en: 'Welcome To' },
  titleTop: { en: 'Citizen Services' },
  titleBottom: { en: 'Admin Portal' },
  lines: [
    { en: 'Review, verify and approve applications,' },
    { en: 'design forms and journeys without code,' },
    { en: 'and keep every decision fully audited.' },
  ],
  features: [
    { icon: 'studio', label: { en: 'No-code Studio' } },
    { icon: 'approvals', label: { en: 'Approvals & SLAs' } },
    { icon: 'reports', label: { en: 'Reports & Analytics' } },
    { icon: 'security', label: { en: 'RBAC & Audit Trail' } },
  ],
  cardTitle: { en: 'Staff & Officer Portal' },
  cardTagline: { en: 'Sign in with your official account' },
  footnote: { en: 'Access is logged and audited' },
};

/**
 * Shipped defaults. Both portals fall back to this when the API is unreachable
 * or the setting has never been written, so a login page never renders unbranded
 * — and `GET /branding` on a fresh database returns exactly this.
 */
export const DEFAULT_BRANDING: Branding = {
  version: 1,
  appName: { en: 'Citizen Services Portal', hi: 'नागरिक सेवा पोर्टल' },
  orgName: { en: 'Government of Bihar', hi: 'बिहार सरकार' },
  adminAppName: { en: 'Citizen Services Admin' },
  logo: null,
  logoDark: null,
  background: null,
  theme: DEFAULT_BRANDING_THEME,
  showLanguageSwitcher: true,
  login: { citizen: CITIZEN_LOGIN, admin: ADMIN_LOGIN },
};

/**
 * Pick the renditions of one format, ascending by width — the shape a
 * `<source srcset>` needs. Empty when the image has no rendition in that format.
 */
export function variantsByFormat(
  image: BrandingImage,
  format: BrandingImageFormat,
): BrandingImageVariant[] {
  return image.variants.filter((v) => v.format === format).sort((a, b) => a.width - b.width);
}

/** Formats present on an image, best-compressing first (AVIF before WebP). */
export function imageFormats(image: BrandingImage): BrandingImageFormat[] {
  const present = new Set(image.variants.map((v) => v.format));
  return (['avif', 'webp'] as const).filter((f) => present.has(f));
}
